<section class="mapping-workspace">
  @if (error) {
    <div class="alert alert-danger">{{ error | translate }}</div>
  }
  <div class="mapping-filter-panel">
    <div class="mapping-filter-panel__search">
      <i class="fa-solid fa-magnifying-glass"></i>
      <input class="form-control" [(ngModel)]="search" [placeholder]="'SEARCH_MAPPINGS' | translate" />
    </div>
    <select class="form-select" [(ngModel)]="statusFilter" [attr.aria-label]="'STATUS' | translate">
      <option value="">{{ 'ALL_MAPPING_STATUSES' | translate }}</option>
      @for (status of mappingStatuses; track status) {
        <option [value]="status">{{ status }}</option>
      }
    </select>
    <select class="form-select" [(ngModel)]="validationFilter" [attr.aria-label]="'VALIDATION' | translate">
      <option value="">{{ 'ALL_VALIDATION_STATES' | translate }}</option>
      <option value="valid">{{ 'VALID_MAPPINGS' | translate }}</option>
      <option value="issues">{{ 'MAPPINGS_WITH_ERRORS' | translate }}</option>
    </select>
    <select class="form-select" [(ngModel)]="lineFilter" [attr.aria-label]="'LINE_BEHAVIOUR' | translate">
      <option value="">{{ 'ALL_LINE_BEHAVIOURS' | translate }}</option>
      <option value="creates">{{ 'CREATES_FINANCE_LINE' | translate }}</option>
      <option value="none">{{ 'NO_FINANCE_LINE' | translate }}</option>
    </select>
    <select class="form-select" [(ngModel)]="contextFilter" [attr.aria-label]="'CONTEXT' | translate">
      <option value="">{{ 'ALL_CONTEXTS' | translate }}</option>
      <option value="product">{{ 'PRODUCT_CONTEXT' | translate }}</option>
      <option value="general">{{ 'GENERAL_CONTEXT' | translate }}</option>
    </select>
    <label class="mapping-filter-check">
      <input type="checkbox" [(ngModel)]="blockedOnly" /> {{ 'BLOCKED_ONLY' | translate }}
    </label>
    <button class="btn btn-outline-secondary" type="button" (click)="resetFilters()">
      {{ 'RESET' | translate }}
    </button>
  </div>

  <article class="mapping-table-card">
    <header class="mapping-table-toolbar">
      <div>
        <strong>{{ filteredMappings.length | number }}</strong> {{ 'OF' | translate }} {{ mappings.length | number }}
        {{ 'MAPPINGS_LOWER' | translate }}
      </div>
      <button type="button" class="btn btn-primary btn-sm" (click)="create()">
        <i class="fa-solid fa-plus"></i> {{ 'NEW_MAPPING' | translate }}
      </button>
    </header>
    <div class="table-responsive">
      <table class="table mapping-table align-middle mb-0">
        <thead>
          <tr>
            <th>{{ 'MAPPING_ID' | translate }}</th>
            <th>{{ 'DESCRIPTION' | translate }}</th>
            <th>{{ 'STATUS' | translate }}</th>
            <th>{{ 'VALIDATION' | translate }}</th>
            <th>{{ 'LINE_BEHAVIOUR' | translate }}</th>
            <th>{{ 'CONTEXT' | translate }}</th>
            <th class="text-end">{{ 'BLOCKED_TRANSACTIONS' | translate }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @if (loading) {
            <tr>
              <td colspan="8" class="mapping-empty">
                <span class="spinner-border spinner-border-sm"></span> {{ 'LOADING' | translate }}
              </td>
            </tr>
          } @else {
            @for (view of filteredMappings; track view.mapping.id) {
              <tr [class.table-active]="selectedOriginalId === view.mapping.id">
                <td>
                  <button type="button" class="mapping-link" (click)="select(view)">{{ view.mapping.id }}</button>
                </td>
                <td class="mapping-description">
                  {{
                    view.mapping.functionalDescription ||
                      view.mapping.descriptionPrefix ||
                      ('NO_DESCRIPTION' | translate)
                  }}
                </td>
                <td>
                  <span class="mapping-pill" [class.mapping-pill--complete]="view.mapping.status === 'COMPLETE'">{{
                    view.mapping.status || '—'
                  }}</span>
                </td>
                <td>
                  @if (!view.validation.valid) {
                    <span class="mapping-pill mapping-pill--danger">
                      {{ view.validation.errors.length }} {{ 'ERRORS' | translate }}
                    </span>
                  } @else if (view.validation.warnings.length) {
                    <span class="mapping-pill mapping-pill--warning">
                      {{ view.validation.warnings.length }} {{ 'WARNINGS' | translate }}
                    </span>
                  } @else {
                    <span class="mapping-pill mapping-pill--complete">{{ 'VALID' | translate }}</span>
                  }
                </td>
                <td>
                  {{ (view.mapping.shouldCreateLine ? 'CREATES_FINANCE_LINE' : 'NO_FINANCE_LINE') | translate }}
                </td>
                <td>
                  {{ (view.mapping.productContext ? 'PRODUCT_CONTEXT' : 'GENERAL_CONTEXT') | translate }}
                  @if (view.mapping.amazonBuying) {
                    <small>{{ 'AMAZON_BUYING_SIDE' | translate }}</small>
                  }
                </td>
                <td class="blocked-count text-end" [class.has-blocked]="view.blockedTransactions > 0">
                  {{ view.blockedTransactions | number }}
                </td>
                <td class="open-column">
                  <div class="mapping-row-actions">
                    @if (view.blockedTransactions > 0) {
                      <button
                        type="button"
                        class="btn btn-sm btn-ai"
                        (click)="selectAndSuggest(view)"
                        [title]="'SUGGEST_MAPPING_WITH_AI' | translate"
                      >
                        <i class="fa-solid fa-wand-magic-sparkles"></i>
                      </button>
                    }
                    <button
                      type="button"
                      class="btn btn-sm btn-light"
                      (click)="select(view)"
                      [title]="'EDIT_MAPPING' | translate"
                    >
                      <i class="fa-solid fa-chevron-right"></i>
                    </button>
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="8" class="mapping-empty">{{ 'NO_MATCHING_MAPPINGS' | translate }}</td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  </article>

  @if (selected) {
    <div class="mapping-editor-backdrop" (click)="closeEditor()"></div>
    <aside class="mapping-editor">
      <header>
        <div>
          <span class="eyebrow">{{
            selectedOriginalId ? ('EDIT_MAPPING' | translate) : ('NEW_MAPPING' | translate)
          }}</span>
          <h2>{{ selected.id || ('UNTITLED_MAPPING' | translate) }}</h2>
        </div>
        <div class="mapping-editor__header-actions">
          @if (selectedOriginalId) {
            <button type="button" class="btn btn-ai btn-sm" (click)="suggestMapping()" [disabled]="suggesting">
              @if (suggesting) {
                <span class="spinner-border spinner-border-sm"></span>
              } @else {
                <i class="fa-solid fa-wand-magic-sparkles"></i>
              }
              {{ 'SUGGEST_MAPPING_WITH_AI' | translate }}
            </button>
          }
          <button type="button" class="btn-close" (click)="closeEditor()"></button>
        </div>
      </header>

      <div class="editor-mode-switch" role="group" [attr.aria-label]="'EDITOR_MODE' | translate">
        <button
          type="button"
          class="btn btn-sm"
          [class.btn-primary]="editorMode === 'guided'"
          [class.btn-light]="editorMode !== 'guided'"
          (click)="editorMode = 'guided'"
        >
          <i class="fa-solid fa-route"></i> {{ 'GUIDED_EDIT' | translate }}
        </button>
        <button
          type="button"
          class="btn btn-sm"
          [class.btn-primary]="editorMode === 'raw'"
          [class.btn-light]="editorMode !== 'raw'"
          (click)="editorMode = 'raw'"
        >
          <i class="fa-solid fa-sliders"></i> {{ 'RAW_EDIT' | translate }}
        </button>
      </div>

      @if (error) {
        <div class="alert alert-danger">{{ error | translate }}</div>
      }

      @if (selectedOriginalId) {
        <section class="mapping-evidence">
          <div>
            <span class="eyebrow">{{ 'MAPPING_EVIDENCE' | translate }}</span>
            <strong>{{ (selectedView?.blockedTransactions || suggestion?.blockedTransactions || 0) | number }}</strong>
            <span>{{ 'BLOCKED_TRANSACTIONS' | translate }}</span>
          </div>
          @if (selected.mappingCreationSource) {
            <div>
              <span>{{ 'DISCOVERED_FROM' | translate }}</span>
              <code>{{ selected.mappingCreationSource }}</code>
            </div>
          }
          @if (selected.mappingCreationSourceRelatedMappings?.length) {
            <details>
              <summary>{{ 'RELATED_MAPPINGS' | translate }} ({{ selected.mappingCreationSourceRelatedMappings?.length }})</summary>
              <code>{{ selected.mappingCreationSourceRelatedMappings?.join(', ') }}</code>
            </details>
          }
          @if (evidenceTransactionIds.length) {
            <div class="mapping-evidence__transactions">
              <span>{{ 'SOURCE_TRANSACTIONS' | translate }}</span>
              <small>{{ 'TRANSACTION_EVIDENCE_HELP' | translate }}</small>
              <div>
                @for (transactionId of evidenceTransactionIds; track transactionId) {
                  <button type="button" (click)="inspectTransaction(transactionId)">
                    <i class="fa-solid fa-magnifying-glass"></i>
                    <code>{{ transactionId }}</code>
                  </button>
                }
              </div>
            </div>
          }
          @if (canDeleteOrphanPlaceholder()) {
            <div class="mapping-evidence__orphan">
              <i class="fa-solid fa-box-archive"></i>
              <div>
                <strong>{{ 'ORPHAN_MAPPING_PLACEHOLDER' | translate }}</strong>
                <span>{{ 'ORPHAN_MAPPING_PLACEHOLDER_HELP' | translate }}</span>
              </div>
            </div>
          }
        </section>
      }

      @if (saved) {
        <div class="alert alert-success py-2">{{ 'MAPPING_SAVED' | translate }}</div>
      }
      @if (completionResult) {
        <div class="alert py-2" [ngClass]="completionResult.automaticRemapSupported === false ? 'alert-warning' : 'alert-success'">
          @if (completionResult.automaticRemapSupported === false) {
            {{ 'MAPPING_COMPLETED_LEGACY_BACKEND' | translate }}
          } @else {
            <strong>{{ completionResult.remapResult.updated.length | number }}</strong>
            {{ (completionResult.remapResult.command === 'QUEUE_REMAP' ? 'TRANSACTIONS_QUEUED' : 'TRANSACTIONS_REMAPPED') | translate }}
            <span>·</span>
            <strong>{{ completionResult.remainingBlockedTransactions | number }}</strong>
            {{ 'STILL_BLOCKED' | translate }}
            @if (completionResult.remapResult.command === 'QUEUE_REMAP') {
              <p class="mb-0">{{ 'MAPPING_QUEUED_HINT' | translate }}</p>
            }
            @if (completionResult.truncated) {
              <span>· {{ 'MORE_TRANSACTIONS_WAITING' | translate }}</span>
            }
          }
        </div>
      }
      @if (suggestionApplied) {
        <div class="alert alert-info py-2 ai-applied-notice">
          <i class="fa-solid fa-circle-info"></i> {{ 'AI_SUGGESTION_APPLIED_HELP' | translate }}
        </div>
      }
      @if (suggestionError) {
        <div class="alert alert-danger py-2">{{ suggestionError | translate }}</div>
      }
      @if (suggestion) {
        <section class="ai-suggestion">
          <header class="ai-suggestion__header">
            <div>
              <span class="eyebrow">{{ 'AI_MAPPING_PROPOSAL' | translate }}</span>
              <h3>{{ 'REVIEW_BEFORE_APPLYING' | translate }}</h3>
            </div>
            <span class="confidence" [ngClass]="'confidence--' + suggestion.confidence.toLowerCase()">
              {{ 'CONFIDENCE_' + suggestion.confidence | translate }}
            </span>
          </header>

          <p class="ai-suggestion__explanation">{{ suggestion.explanation }}</p>
          <div class="ai-suggestion__impact">
            <span
              ><strong>{{ suggestion.blockedTransactions | number }}</strong>
              {{ 'BLOCKED_TRANSACTIONS' | translate }}</span
            >
            <span
              ><strong>{{ suggestion.evidenceTransactionIds.length }}</strong>
              {{ 'SAMPLE_TRANSACTIONS' | translate }}</span
            >
          </div>

          @if (suggestion.validation.errors.length || suggestion.warnings.length) {
            <div class="ai-suggestion__warnings">
              @for (message of suggestion.validation.errors; track message) {
                <p><i class="fa-solid fa-circle-xmark"></i> {{ message }}</p>
              }
              @for (message of suggestion.warnings; track message) {
                <p><i class="fa-solid fa-triangle-exclamation"></i> {{ message }}</p>
              }
            </div>
          }

          <div class="ai-diff">
            <div class="ai-diff__head">
              <span>{{ 'FIELD' | translate }}</span>
              <span>{{ 'CURRENT_VALUE' | translate }}</span>
              <span>{{ 'PROPOSED_VALUE' | translate }}</span>
            </div>
            @for (change of suggestionChanges(); track change.field) {
              <div class="ai-diff__row">
                <strong>{{ change.field }}</strong>
                <span>{{ change.before }}</span>
                <span>{{ change.after }}</span>
              </div>
            } @empty {
              <p class="ai-diff__empty">{{ 'AI_NO_MAPPING_CHANGES' | translate }}</p>
            }
          </div>

          @if (suggestion.alternatives.length) {
            <details class="ai-alternatives">
              <summary>{{ 'ALTERNATIVE_SUGGESTIONS' | translate }} ({{ suggestion.alternatives.length }})</summary>
              @for (alternative of suggestion.alternatives; track $index) {
                <article>
                  <div>
                    <strong>{{ alternative.label || ('ALTERNATIVE' | translate) }}</strong>
                    <p>{{ alternative.explanation }}</p>
                  </div>
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    [disabled]="!alternative.proposedMapping"
                    (click)="applyAlternative(alternative)"
                  >
                    {{ 'USE_ALTERNATIVE' | translate }}
                  </button>
                </article>
              }
            </details>
          }

          <details class="ai-evidence">
            <summary>{{ 'EVIDENCE_TRANSACTIONS' | translate }}</summary>
            <div class="ai-evidence__transactions">
              @for (transactionId of suggestion.evidenceTransactionIds; track transactionId) {
                <button type="button" (click)="inspectTransaction(transactionId)">
                  <i class="fa-solid fa-magnifying-glass"></i> {{ transactionId }}
                </button>
              } @empty {
                <code>—</code>
              }
            </div>
          </details>

          <footer class="ai-suggestion__actions">
            <button type="button" class="btn btn-outline-secondary btn-sm" (click)="dismissSuggestion()">
              {{ 'DISMISS' | translate }}
            </button>
            <button type="button" class="btn btn-primary btn-sm" (click)="applySuggestion()">
              {{ 'APPLY_AI_DRAFT' | translate }}
            </button>
          </footer>
        </section>
      }
      @if (validation?.errors?.length) {
        <div class="validation validation--error">
          <strong>{{ 'FIX_BEFORE_SAVING' | translate }}</strong>
          <ul>
            @for (message of validation.errors; track message) {
              <li>{{ message }}</li>
            }
          </ul>
        </div>
      }
      @if (validation?.warnings?.length) {
        <div class="validation validation--warning">
          <strong>{{ 'REVIEW_WARNINGS' | translate }}</strong>
          <ul>
            @for (message of validation.warnings; track message) {
              <li>{{ message }}</li>
            }
          </ul>
        </div>
      }

      @if (editorMode === 'guided') {
        <div class="form-section">
          <h3>{{ 'STEP_DESCRIBE_MAPPING' | translate }}</h3>
          <label
            ><span>{{ 'MAPPING_ID' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.id" [readonly]="!!selectedOriginalId"
          /></label>
          <label
            ><span>{{ 'FUNCTIONAL_DESCRIPTION' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.functionalDescription"
          /></label>
        </div>

        <div class="form-section">
          <h3>{{ 'STEP_CHOOSE_BEHAVIOUR' | translate }}</h3>
          <div class="workflow-choice-grid">
            <button
              type="button"
              [class.active]="workflowIntent() === 'CREATE_LINE'"
              (click)="setWorkflowIntent('CREATE_LINE')"
            >
              <i class="fa-solid fa-file-invoice-dollar"></i>
              <strong>{{ 'WORKFLOW_CREATE_LINE' | translate }}</strong>
              <span>{{ 'WORKFLOW_CREATE_LINE_HELP' | translate }}</span>
            </button>
            <button
              type="button"
              [class.active]="workflowIntent() === 'REFERENCE_ONLY'"
              (click)="setWorkflowIntent('REFERENCE_ONLY')"
            >
              <i class="fa-solid fa-link"></i>
              <strong>{{ 'WORKFLOW_REFERENCE_ONLY' | translate }}</strong>
              <span>{{ 'WORKFLOW_REFERENCE_ONLY_HELP' | translate }}</span>
            </button>
            <button
              type="button"
              [class.active]="workflowIntent() === 'IGNORE'"
              (click)="setWorkflowIntent('IGNORE')"
            >
              <i class="fa-solid fa-ban"></i>
              <strong>{{ 'WORKFLOW_IGNORE' | translate }}</strong>
              <span>{{ 'WORKFLOW_IGNORE_HELP' | translate }}</span>
            </button>
            <button
              type="button"
              [class.active]="workflowIntent() === 'CUSTOM'"
              (click)="setWorkflowIntent('CUSTOM'); editorMode = 'raw'"
            >
              <i class="fa-solid fa-sliders"></i>
              <strong>{{ 'WORKFLOW_CUSTOM' | translate }}</strong>
              <span>{{ 'WORKFLOW_CUSTOM_HELP' | translate }}</span>
            </button>
          </div>
        </div>

        @if (workflowIntent() === 'CREATE_LINE') {
          <div class="form-section">
            <h3>{{ 'STEP_ACCOUNTING_SIDE' | translate }}</h3>
            <div class="workflow-choice-grid workflow-choice-grid--two">
              <button type="button" [class.active]="selected.amazonBuying" (click)="selected.amazonBuying = true">
                <strong>{{ 'CUSTOMER_REVENUE' | translate }}</strong>
                <span>{{ 'CUSTOMER_REVENUE_SIDE_HELP' | translate }}</span>
              </button>
              <button type="button" [class.active]="!selected.amazonBuying" (click)="selected.amazonBuying = false">
                <strong>{{ 'AMAZON_FEES' | translate }}</strong>
                <span>{{ 'AMAZON_FEE_SIDE_HELP' | translate }}</span>
              </button>
            </div>
            <div class="switch-grid workflow-switches">
              <label
                ><input type="checkbox" [(ngModel)]="selected.productContext" />
                <span>{{ 'PRODUCT_CONTEXT' | translate }}</span></label
              >
              <label
                ><input type="checkbox" [(ngModel)]="selected.impliedVAT" />
                <span>{{ 'IMPLIED_VAT' | translate }}</span></label
              >
            </div>
          </div>

          <div class="form-section form-grid">
            <h3>{{ 'STEP_TARGET_VALUES' | translate }}</h3>
            <label
              ><span>{{ 'PRODUCT_ID_OVERRIDE' | translate }}</span
              ><input class="form-control" [(ngModel)]="selected.productIdOverride"
            /></label>
            <label
              ><span>{{ 'ACCOUNT_NUMBER_OVERRIDE' | translate }}</span
              ><input class="form-control" type="number" [(ngModel)]="selected.accountNumberOverride"
            /></label>
            <label
              ><span>{{ 'TAX_PERCENT_OVERRIDE' | translate }}</span
              ><input class="form-control" type="number" step="0.01" [(ngModel)]="selected.taxPercentOverride"
            /></label>
            <label
              ><span>{{ 'DESCRIPTION_PREFIX' | translate }}</span
              ><input class="form-control" [(ngModel)]="selected.descriptionPrefix"
            /></label>
          </div>
        }

        @if (workflowIntent() === 'CREATE_LINE' || workflowIntent() === 'REFERENCE_ONLY') {
          <div class="form-section form-grid">
            <h3>{{ 'STEP_RELATIONSHIPS' | translate }}</h3>
            <label
              ><span>{{ 'TAX_ELEMENT_KEY' | translate }}</span
              ><input class="form-control" [(ngModel)]="selected.taxElementKey"
            /></label>
            <label
              ><span>{{ 'TAX_CANCELLING_KEY' | translate }}</span
              ><input class="form-control" [(ngModel)]="selected.taxCancellingElementKey"
            /></label>
            <label class="full-width"
              ><span>{{ 'AMOUNT_DEDUCTING_KEYS_HELP' | translate }}</span
              ><textarea class="form-control" rows="3" [(ngModel)]="deductingKeys"></textarea>
            </label>
          </div>
        }

        @if (workflowIntent() === 'IGNORE') {
          <div class="workflow-ignore-notice">
            <i class="fa-solid fa-circle-info"></i>
            <div>
              <strong>{{ 'WORKFLOW_IGNORE_CONFIRMATION' | translate }}</strong>
              <p>{{ 'WORKFLOW_IGNORE_CONFIRMATION_HELP' | translate }}</p>
            </div>
          </div>
        }
      } @else {
        <div class="form-section">
          <h3>{{ 'IDENTITY_AND_BEHAVIOUR' | translate }}</h3>
          <label
            ><span>{{ 'MAPPING_ID' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.id" [readonly]="!!selectedOriginalId"
          /></label>
          <label
            ><span>{{ 'FUNCTIONAL_DESCRIPTION' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.functionalDescription"
          /></label>
          <label
            ><span>{{ 'MAPPING_PURPOSE' | translate }}</span
            ><select class="form-select" [(ngModel)]="selected.workflowIntent">
              <option [ngValue]="null">—</option>
              <option value="CREATE_LINE">CREATE_LINE</option>
              <option value="REFERENCE_ONLY">REFERENCE_ONLY</option>
              <option value="IGNORE">IGNORE</option>
              <option value="CUSTOM">CUSTOM</option>
            </select></label
          >
          <label
            ><span>{{ 'STATUS' | translate }}</span
            ><select class="form-select" [(ngModel)]="selected.status">
              <option value="COMPLETE">COMPLETE</option>
              <option value="INCOMPLETE">INCOMPLETE</option>
              <option value="DISABLED">DISABLED</option>
            </select></label
          >
          <div class="switch-grid">
            <label
              ><input type="checkbox" [(ngModel)]="selected.shouldCreateLine" />
              <span>{{ 'CREATE_FINANCE_LINE' | translate }}</span></label
            >
            <label
              ><input type="checkbox" [(ngModel)]="selected.productContext" />
              <span>{{ 'PRODUCT_CONTEXT' | translate }}</span></label
            >
            <label
              ><input type="checkbox" [(ngModel)]="selected.amazonBuying" />
              <span>{{ 'AMAZON_BUYING_SIDE' | translate }}</span></label
            >
            <label
              ><input type="checkbox" [(ngModel)]="selected.impliedVAT" />
              <span>{{ 'IMPLIED_VAT' | translate }}</span></label
            >
          </div>
        </div>

        <div class="form-section form-grid">
          <h3>{{ 'TARGET_OVERRIDES' | translate }}</h3>
          <label
            ><span>{{ 'PRODUCT_ID_OVERRIDE' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.productIdOverride"
          /></label>
          <label
            ><span>{{ 'ACCOUNT_NUMBER_OVERRIDE' | translate }}</span
            ><input class="form-control" type="number" [(ngModel)]="selected.accountNumberOverride"
          /></label>
          <label
            ><span>{{ 'TAX_PERCENT_OVERRIDE' | translate }}</span
            ><input class="form-control" type="number" step="0.01" [(ngModel)]="selected.taxPercentOverride"
          /></label>
          <label
            ><span>{{ 'PRIORITY' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.priority"
          /></label>
          <label
            ><span>{{ 'DESCRIPTION_PREFIX' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.descriptionPrefix"
          /></label>
          <label
            ><span>{{ 'SUFFIX' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.suffix"
          /></label>
        </div>

        <div class="form-section form-grid">
          <h3>{{ 'TAX_AND_DEPENDENCIES' | translate }}</h3>
          <label
            ><span>{{ 'TAX_ELEMENT_KEY' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.taxElementKey"
          /></label>
          <label
            ><span>{{ 'TAX_CANCELLING_KEY' | translate }}</span
            ><input class="form-control" [(ngModel)]="selected.taxCancellingElementKey"
          /></label>
          <label class="full-width"
            ><span>{{ 'AMOUNT_DEDUCTING_KEYS_HELP' | translate }}</span
            ><textarea class="form-control" rows="3" [(ngModel)]="deductingKeys"></textarea>
          </label>
        </div>
      }

      <footer>
        <button type="button" class="btn btn-outline-secondary" (click)="validate()">
          {{ 'VALIDATE' | translate }}
        </button>
        @if (canDeleteOrphanPlaceholder()) {
          <button type="button" class="btn btn-outline-danger me-auto" (click)="deleteOrphanPlaceholder()">
            <span *ngIf="deleting" class="spinner-border spinner-border-sm"></span>
            <i *ngIf="!deleting" class="fa-solid fa-trash-can"></i>
            {{ 'REMOVE_PLACEHOLDER' | translate }}
          </button>
        }
        @if (editorMode === 'guided') {
          <button type="button" class="btn btn-outline-primary" [disabled]="saving || !selected.id" (click)="saveDraft()">
            <span *ngIf="saving" class="spinner-border spinner-border-sm"></span> {{ 'SAVE_DRAFT' | translate }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            [disabled]="completing || !selected.id || !selected.workflowIntent || !!validation?.errors?.length"
            (click)="completeAndQueue()"
          >
            <span *ngIf="completing" class="spinner-border spinner-border-sm"></span>
            {{ 'COMPLETE_AND_QUEUE' | translate }}
          </button>
        } @else {
          <button type="button" class="btn btn-primary" [disabled]="saving || !selected.id" (click)="save()">
            <span *ngIf="saving" class="spinner-border spinner-border-sm"></span> {{ 'SAVE_MAPPING' | translate }}
          </button>
        }
      </footer>
    </aside>

    @if (transactionInspectionLoading || inspectedTransaction || transactionInspectionError) {
      <div class="source-inspector-backdrop" (click)="closeTransactionInspector()"></div>
      <aside class="source-inspector">
        @if (transactionInspectionLoading) {
          <div class="source-inspector__loading">
            <span class="spinner-border"></span>
            <span>{{ 'LOADING' | translate }}</span>
          </div>
        } @else if (transactionInspectionError) {
          <header>
            <h2>{{ 'SOURCE_TRANSACTION' | translate }}</h2>
            <button type="button" class="btn-close" (click)="closeTransactionInspector()"></button>
          </header>
          <div class="alert alert-danger m-3">{{ transactionInspectionError | translate }}</div>
        } @else if (inspectedTransaction) {
          <header>
            <div>
              <span class="eyebrow">{{ 'SOURCE_TRANSACTION' | translate }}</span>
              <h2>{{ inspectedTransaction.summary.transactionType || '—' }}</h2>
              <code>{{ inspectedTransaction.summary.id }}</code>
            </div>
            <button type="button" class="btn-close" (click)="closeTransactionInspector()"></button>
          </header>

          <div class="source-inspector__summary">
            <div>
              <span>{{ 'POSTED_DATE' | translate }}</span>
              <strong>{{ inspectedTransaction.summary.postedDate | date: 'yyyy-MM-dd HH:mm' }}</strong>
            </div>
            <div>
              <span>{{ 'MARKETPLACE' | translate }}</span>
              <strong>{{ inspectedTransaction.summary.marketplace || '—' }}</strong>
            </div>
            <div>
              <span>{{ 'AMOUNT' | translate }}</span>
              <strong
                >{{ inspectedTransaction.summary.amount | number: '1.2-2' }}
                {{ inspectedTransaction.summary.currency }}</strong
              >
            </div>
            <div>
              <span>{{ 'MAPPING' | translate }}</span>
              <strong>{{ inspectedTransaction.summary.mappingStatus || '—' }}</strong>
            </div>
          </div>

          @if (inspectedTransaction.summary.missingMappings.length) {
            <section class="source-inspector__missing">
              <strong>{{ 'MISSING_MAPPINGS' | translate }}</strong>
              <div>
                @for (mappingId of inspectedTransaction.summary.missingMappings; track mappingId) {
                  <code [class.active]="mappingId === selected.id">{{ mappingId }}</code>
                }
              </div>
            </section>
          }

          <details open>
            <summary>{{ 'RAW_AMAZON_SOURCE' | translate }}</summary>
            <pre>{{ inspectedTransaction.source | json }}</pre>
          </details>
          <details>
            <summary>{{ 'NORMALIZED_ELEMENTS' | translate }}</summary>
            <pre>{{ inspectedTransaction.normalizedElements | json }}</pre>
          </details>
        }
      </aside>
    }
  }
</section>
